import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Modal from "./UI/Modal";

const Resources = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isMultimediaModalOpen, setIsMultimediaModalOpen] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const resources = [
    {
      id: "toolkits",
      title: t("resources.types.toolkits.title"),
      description: t("resources.types.toolkits.description"),
      action: t("resources.types.toolkits.action"),
      isDisabled: true,
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
          />
        </svg>
      ),
      color: "from-emerald-500 to-teal-500",
      bgColor: "from-emerald-50 to-teal-50",
    },
    {
      id: "research",
      title: t("resources.types.research.title"),
      description: t("resources.types.research.description"),
      action: t("resources.types.research.action"),
      isDisabled: true,
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
      color: "from-teal-500 to-cyan-500",
      bgColor: "from-teal-50 to-cyan-50",
    },
    {
      id: "multimedia",
      title: t("resources.types.multimedia.title"),
      description: t("resources.types.multimedia.description"),
      action: t("resources.types.multimedia.action"),
      isDisabled: false,
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M15 10l4.553-2.276A2 2 0 0020 6.382V5a2 2 0 00-2-2H6a2 2 0 00-2 2v1.382a2 2 0 00.447 1.342L9 10m6 0v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4m6 0l-6-3"
          />
        </svg>
      ),
      color: "from-emerald-500 to-teal-500",
      bgColor: "from-emerald-50 to-teal-50",
      isNavigation: true,
      path: "/media",
    },
  ];

  return (
    <section
      ref={sectionRef}
      id="resources"
      className="py-20 relative overflow-hidden"
      style={{ backgroundColor: "var(--background-color)" }}
    >
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-50/30 via-transparent to-purple-50/30"></div>
        <div className="absolute top-20 right-20 w-72 h-72 bg-gradient-to-r from-blue-200/20 to-indigo-200/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-r from-purple-200/20 to-pink-200/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 md:px-8">
        {/* Section header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t("resources.title")}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto rounded-full mb-8"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Access our comprehensive collection of tools, research, and
            multimedia resources
          </p>
        </div>

        {/* Notice banner */}
        <div
          className={`mb-16 transition-all duration-1000 delay-300 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-400 rounded-2xl p-6 shadow-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-yellow-800 font-medium text-lg">
                  {t("resources.notice")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Resources grid */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-1000 delay-500 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          {resources.map((resource, index) => (
            <div
              key={resource.id}
              className={`group relative bg-white rounded-3xl p-8 shadow-lg border border-gray-100 transition-all duration-300 flex flex-col h-full ${
                resource.isDisabled
                  ? "cursor-not-allowed opacity-75"
                  : "cursor-pointer hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-2"
              }`}
              onClick={() => {
                if (!resource.isDisabled) {
                  if (resource.isModal) {
                    setIsMultimediaModalOpen(true);
                  } else if (resource.isNavigation) {
                    navigate(resource.path);
                  }
                }
              }}
            >
              {/* Subtle hover background */}
              {!resource.isDisabled && (
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${resource.bgColor} opacity-0 group-hover:opacity-30 transition-all duration-300 rounded-3xl`}
                ></div>
              )}

              {/* Content */}
              <div className="relative z-10 flex flex-col h-full">
                {/* Icon and title */}
                <div className="flex items-center mb-6">
                  <div
                    className={`w-16 h-16 bg-gradient-to-r ${resource.color} rounded-2xl flex items-center justify-center text-white mr-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg group-hover:shadow-xl`}
                  >
                    {resource.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 group-hover:text-gray-800 transition-colors duration-300">
                    {resource.title}
                  </h3>
                </div>

                {/* Description */}
                <p className="text-lg leading-relaxed mb-6 text-gray-600 group-hover:text-gray-700 transition-colors duration-300 flex-grow">
                  {resource.description}
                </p>

                {/* Action button and indicator at bottom */}
                <div className="flex items-center justify-between mt-auto">
                  <button
                    disabled={resource.isDisabled}
                    className={`inline-flex items-center font-semibold transition-all duration-300 group/btn px-4 py-2 rounded-lg ${
                      resource.isDisabled
                        ? "text-gray-400 cursor-not-allowed"
                        : `hover:bg-white/50 ${
                            resource.id === "toolkits"
                              ? "text-emerald-600 hover:text-emerald-700"
                              : resource.id === "research"
                              ? "text-teal-600 hover:text-teal-700"
                              : "text-emerald-600 hover:text-emerald-700"
                          }`
                    }`}
                  >
                    <span className="mr-2">{resource.action}</span>
                    {!resource.isDisabled && (
                      <svg
                        className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    )}
                  </button>

                  {/* Resource type indicator */}
                  <div className="flex items-center space-x-2">
                    <div
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        resource.id === "toolkits"
                          ? "bg-emerald-500"
                          : resource.id === "research"
                          ? "bg-teal-500"
                          : "bg-emerald-500"
                      }`}
                    ></div>
                    <span className="text-sm text-gray-500 font-medium">
                      {resource.id === "toolkits"
                        ? "Tools"
                        : resource.id === "research"
                        ? "Research"
                        : "Media"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Border highlight on hover */}
              <div
                className={`absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-gradient-to-r group-hover:${resource.color} group-hover:opacity-20 transition-all duration-300`}
              ></div>
            </div>
          ))}
        </div>
      </div>

      {/* Multimedia Modal */}
      <Modal
        isOpen={isMultimediaModalOpen}
        onClose={() => setIsMultimediaModalOpen(false)}
        title="Multimedia Resources"
        size="md"
      >
        <div className="space-y-4">
          <a
            href="https://drive.google.com/drive/folders/1yZaU3Z7ojcnsbpI2hBrOJ_ZnBJ0YwimQ"
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 transition-all duration-300 rounded-xl p-6 text-emerald-700 font-semibold shadow-sm hover:shadow-md text-center group"
          >
            <div className="flex items-center justify-center gap-3">
              <svg
                className="w-6 h-6 text-emerald-600 group-hover:scale-110 transition-transform duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 10l4.553-2.276A2 2 0 0020 6.382V5a2 2 0 00-2-2H6a2 2 0 00-2 2v1.382a2 2 0 00.447 1.342L9 10m6 0v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4m6 0l-6-3"
                />
              </svg>
              <span>Romania Training Photos</span>
              <svg
                className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </div>
          </a>

          <a
            href="https://drive.google.com/drive/folders/1q_462RJk1Lf72pLPH3UO74KyCUVsavdy"
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-gradient-to-r from-yellow-50 to-orange-50 hover:from-yellow-100 hover:to-orange-100 transition-all duration-300 rounded-xl p-6 text-yellow-700 font-semibold shadow-sm hover:shadow-md text-center group"
          >
            <div className="flex items-center justify-center gap-3">
              <svg
                className="w-6 h-6 text-yellow-600 group-hover:scale-110 transition-transform duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 10l4.553-2.276A2 2 0 0020 6.382V5a2 2 0 00-2-2H6a2 2 0 00-2 2v1.382a2 2 0 00.447 1.342L9 10m6 0v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4m6 0l-6-3"
                />
              </svg>
              <span>Kickoff Meeting Photos</span>
              <svg
                className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </div>
          </a>
        </div>
      </Modal>
    </section>
  );
};

export default Resources;
